import { Card, ConfirmModal, Modal, RsInput } from '@/components/Bicester';
import { fetchAddEditLose, fetchLoseInfo } from '@/services/site/lAndF';
import React, { useState } from 'react';
import { BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import { defaultLoseEditInfo, loseEditSchema } from '../../LostAndFound/constants';
import { isBefore, subDays } from 'date-fns';
import ServiceInfo from '../../components/ServiceInfo';
import useLoseBasic from '@/hooks/preload/useLoseBasic';
import Image from '@/components/Bicester/Image';
import { Alert } from 'rsuite';
import { IconButton } from 'rsuite5';
import { LEVEL_TYPE } from '@/constants';
import { fetchServiceMemberInfo } from '@/services/site';
import { useRequest } from 'ahooks';
import { useModal } from '@ebay/nice-modal-react';
import { getPathname, push } from '@/utils/history';
import { ACCOUNT_TABS } from '@/views/CustomerAccount';
import _ from 'lodash';
import useGePcsList from '@/hooks/preload/useGePcsList';
import { SERVICE_STATUS } from '@/services/constants';

const LoseInfo = () => {
  const modal = useModal(ConfirmModal);
  const { data: loseBasic } = useLoseBasic();
  const { data: gePcsList } = useGePcsList();

  const [content, setContent] = useState('');
  const [showModal, setShowModal] = useState(false);

  const {
    run: getServiceMemberInfo,
    data: serviceMemberInfoData,
  } = useRequest(fetchServiceMemberInfo, {
    manual: true,
  });

  return (
    <ServiceInfo
      defaultServiceInfo={defaultLoseEditInfo}
      serviceSchema={loseEditSchema}
      fetchInfo={fetchLoseInfo}
      fetchEdit={fetchAddEditLose}
      getDefaultInfo={(data) => {
        return {
          item_no: data?.item_no,
          item_type: data?.item_type,
          item_is_valuable: data?.item_is_valuable,
          item_pick_addr: data?.item_pick_addr,
          item_deposit_addr_id: data?.item_deposit_addr_id,
          item_validity_date: data?.item_validity_date,
          item_remark: data?.item_remark,
          lose_status: data?.lose_status,
          member_id: data?.member_id || 0,
          not_member_id: data?.not_member_id || 0,
          user_name: data?.user_name,
          phone: data?.phone,
          record_creator: data?.record_creator,
          record_close: data?.record_close,
          pick_name: data?.pick_name,
          pick_date_at: data?.pick_date_at,
          pick_team_id: data?.pick_team_id,
          pick_remark: data?.pick_remark,
        };
      }}
      getSubmitData={(id, values) =>
        Object.assign({}, _.omit(values, 'phone'), {
          invoice_items: JSON.stringify(values?.invoice_items),
          id,
        })
      }
      basicInfoTitle="物品信息"
      basicInfo={({ formik, infoData, isEdit }) => (
        <>
          <Card.Input isEdit={isEdit} label="物品编号" formKey="item_no" />
          <Card.Picker
            required
            isEdit={isEdit}
            label="物品类型"
            formKey="item_type"
            pickerData={loseBasic.item_type}
            searchable
          />
          <Card.Picker
            isEdit={isEdit}
            label="是否为高额物品"
            formKey="item_is_valuable"
            pickerType="TorF"
          />
          <Card.Input isEdit={isEdit} label="物品找到/失踪地点" formKey="item_pick_addr" />
          <Card.Picker
            isEdit={isEdit}
            label="存放地点"
            formKey="item_deposit_addr_id"
            pickerData={loseBasic.deposit_addr}
            value={infoData?.item_deposit_addr}
            searchable
          />
          <Card.DatePicker
            isEdit={isEdit}
            label="物品到期日"
            formKey="item_validity_date"
            disabledDate={(date) => isBefore(date, subDays(new Date(), 1))}
          />
          <Card.Input className="flex-[2]" isEdit={isEdit} label="物品描述" formKey="item_remark" />
        </>
      )}
      serviceInfo={({ formik, infoData, isEdit }) => (
        <>
          <Card.Input
            notEditable
            label="服务地点"
            formKey="servicesite_name"
            value={infoData?.servicesite_name}
          />
          <Card.Picker
            required
            isEdit={isEdit}
            label="服务状态"
            formKey="lose_status"
            pickerData={loseBasic.lose_status}
            cleanable={false}
            value={infoData?.lose_status_name}
          />
          <Card.Input // 如果服务状态是失物(1-4)，挂失人/领取人字段必填
            {...(formik.values?.lose_status >= 1 &&
              formik.values?.lose_status <= 4 && {
                required: true,
              })}
            isEdit={isEdit}
            label="挂失人/领取人"
            formKey="user_name"
            cleanable
            onFocus={(handler) => {
              setShowModal(true);
              handler.target.blur();
            }}
            onClean={() =>
              formik.setValues({
                ...formik.values,
                member_id: 0,
                not_member_id: 0,
                user_name: '',
                phone: '',
              })
            }
            autoWidth={!isEdit}
            hasBtn
            showBtn={isEdit || Boolean(infoData?.member_id || infoData?.not_member_id)}
            customBtn={
              isEdit ? (
                <div className="w-[30px] h-[47px] ml-[10px] flex items-center">
                  <IconButton
                    style={{ width: 30, backgroundColor: 'transparent' }}
                    onClick={() => setShowModal(true)}
                  >
                    <Image src="/resources/images/match.svg" />
                  </IconButton>
                </div>
              ) : (
                <IconButton
                  style={{ width: 20, backgroundColor: 'transparent' }}
                  onClick={() => {
                    const isMember = infoData?.level !== LEVEL_TYPE.NOT;
                    window.open(
                      getPathname(
                        `/customer-account/${
                          isMember ? ACCOUNT_TABS.MEMBER : ACCOUNT_TABS.NO_MEMBER
                        }/detail/${isMember ? infoData?.member_id : infoData?.not_member_id}`
                      )
                    );
                  }}
                >
                  <Image
                    src={`/resources/images/user${
                      infoData?.sex === '男' ? '-man' : infoData?.sex === '女' ? '-woman' : ''
                    }.svg`}
                  />
                </IconButton>
              )
            }
          />
          <Card.Input notEditable label="手机号" formKey="phone" />
          <Card.Input // 如果服务状态是招领(5-9)，拾到人姓名字段必填
            {...(formik.values?.lose_status >= 5 &&
              formik.values?.lose_status <= 9 && {
                required: true,
              })}
            isEdit={isEdit}
            label="拾到人"
            formKey="pick_name"
          />
          <Card.Picker
            isEdit={isEdit}
            label="拾到人团队"
            formKey="pick_team_id"
            pickerData={loseBasic.pick_team}
            value={infoData?.pick_team}
          />
          <Card.Input isEdit={isEdit} label="拾到备注" formKey="pick_remark" inputType="textarea" />
          <Card.DatePicker
            isEdit={isEdit}
            label="拾到时间"
            formKey="pick_date_at"
            format={BICESTER_FULL_DATE_HOURS_MINUTES}
            readOnlyFormat={BICESTER_FULL_DATE_HOURS_MINUTES}
          />
          <Card.Picker
            required
            isEdit={isEdit}
            label="记录创建人"
            formKey="record_creator"
            pickerData={gePcsList.ge_list}
            value={infoData?.record_creator_name}
            searchable
          />
          <Card.Picker
            isEdit={isEdit}
            label="记录关闭人"
            formKey="record_close"
            pickerData={gePcsList.ge_list}
            value={infoData?.record_close_name}
            searchable
          />
          <Modal
            show={showModal}
            content={
              <>
                <div className="leading-[30px] text-left">输入手机号/会员号精确匹配</div>
                <RsInput className="h-[30px]" value={content} onChange={setContent} />
              </>
            }
            confirm="开始匹配"
            cancel="取消"
            onConfirm={() => {
              setShowModal(false);
              setContent('');
              getServiceMemberInfo({ content }).then((res) => {
                if (res?.status === SERVICE_STATUS.SUCCESS) {
                  if (res?.data?.id) {
                    Alert.success('匹配成功');
                    const { id, level } = res.data;
                    formik.setValues({
                      ...formik.values,
                      user_name: res?.data?.name ?? '',
                      phone: res?.data?.phone ?? '',
                      ...(level === LEVEL_TYPE.NOT ? { not_member_id: id } : { member_id: id }),
                    });
                  } else {
                    modal.show({
                      contentText: '匹配失败，是否创建非会员账户？',
                      submitText: '是',
                      cancelText: '取消',
                      onSubmit() {
                        push('/customer-account/no-member/create');
                      },
                    });
                  }
                }
              });
            }}
            onCancel={() => {
              setShowModal(false);
              setContent('');
            }}
          />
        </>
      )}
    />
  );
};

export default LoseInfo;
